"use client";
import React, { useRef, useState, useEffect } from 'react';
import {
    motion,
    AnimatePresence,
    useScroll,
    useMotionValueEvent,
} from 'framer-motion';

function useSlideVh() {
    const [vh, setVh] = useState(100);
    useEffect(() => {
        const check = () => setVh(window.innerWidth < 1024 ? 78 : 100);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);
    return vh;
}

/* ─── SVG Icons ─── */
const IconPeopleFirst = ({ color }: { color: string }) => (
    <svg width="72" height="72" viewBox="0 0 80 80" fill="none">
        <circle cx="40" cy="28" r="14" stroke={color} strokeWidth="3.5" strokeLinecap="round"/>
        <path d="M12 68c0-15.464 12.536-28 28-28s28 12.536 28 28" stroke={color} strokeWidth="3.5" strokeLinecap="round"/>
        <path d="M54 34l4 4 8-10" stroke={color} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);
const IconRealGrowth = ({ color }: { color: string }) => (
    <svg width="72" height="72" viewBox="0 0 80 80" fill="none">
        <path d="M12 58L28 38l12 10L60 22" stroke={color} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M48 22h12v12" stroke={color} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="58" cy="62" r="9" stroke={color} strokeWidth="3"/>
        <path d="M55 62l2.5 2.5L62 57" stroke={color} strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);
const IconIndustryFit = ({ color }: { color: string }) => (
    <svg width="72" height="72" viewBox="0 0 80 80" fill="none">
        <rect x="8" y="8" width="27" height="27" rx="6" stroke={color} strokeWidth="3.5"/>
        <rect x="45" y="8" width="27" height="27" rx="6" stroke={color} strokeWidth="3.5"/>
        <rect x="8" y="45" width="27" height="27" rx="6" stroke={color} strokeWidth="3.5"/>
        <rect x="45" y="45" width="27" height="27" rx="6" stroke={color} strokeWidth="3.5"/>
    </svg>
);
const IconFullFunnel = ({ color }: { color: string }) => (
    <svg width="72" height="72" viewBox="0 0 80 80" fill="none">
        <path d="M10 18h60L48 44v20l-16-8V44L10 18z" stroke={color} strokeWidth="3.5" strokeLinejoin="round"/>
    </svg>
);
const IconTransparency = ({ color }: { color: string }) => (
    <svg width="72" height="72" viewBox="0 0 80 80" fill="none">
        <path d="M8 40S20 14 40 14s32 26 32 26-12 26-32 26S8 40 8 40z" stroke={color} strokeWidth="3.5" strokeLinejoin="round"/>
        <circle cx="40" cy="40" r="10" stroke={color} strokeWidth="3.5"/>
        <circle cx="40" cy="40" r="3.5" fill={color}/>
    </svg>
);

/* ─── Slides ─── */
const slides = [
    {
        num: '01', title: 'We Start Where Most Agencies Stop',
        body: 'Most digital marketing agencies begin with platforms, budgets, and targeting. We begin with people. Every successful campaign starts with understanding how your audience thinks, what they value, what they fear, and what makes them choose one business over another.',
        cardTitle: 'People First',
        innerBg: '#ffffff', innerColor: '#080400',
        sectionBg: '#080400',
        Icon: IconPeopleFirst,
    },
    {
        num: '02', title: 'Growth Built On Understanding, Not Assumptions',
        body: 'Businesses often assume they have a traffic problem. In reality, many have a messaging problem, a positioning problem, or a customer understanding problem. We uncover the motivations and decision-making patterns that drive your market.',
        cardTitle: 'Real Growth',
        innerBg: '#ceff1a', innerColor: '#080400',
        sectionBg: '#0b0916',
        Icon: IconRealGrowth,
    },
    {
        num: '03', title: 'Every Industry Buys Differently',
        body: "A homeowner looking for an interior designer doesn't think like a patient researching aesthetic treatments. A SaaS founder doesn't evaluate options the same way a real-estate buyer does. We adapt our framework to how customers actually make decisions within your industry.",
        cardTitle: 'Industry Fit',
        innerBg: '#ffffff', innerColor: '#080400',
        sectionBg: '#080400',
        Icon: IconIndustryFit,
    },
    {
        num: '04', title: 'Full-Funnel Thinking',
        body: 'Campaigns perform best when every part of the customer journey works together. Google Ads, Meta Ads, landing pages, CRO, analytics, and customer experience all influence performance. We build connected systems that turn attention into enquiries, leads, and revenue.',
        cardTitle: 'Full Funnel',
        innerBg: '#ceff1a', innerColor: '#080400',
        sectionBg: '#0b0916',
        Icon: IconFullFunnel,
    },
    {
        num: '05', title: 'Radical Transparency',
        body: "You deserve to know exactly where your budget is going and what's driving results. Through live dashboards and clear reporting, you'll always understand what's working, what isn't, and what we're doing next.",
        cardTitle: 'Transparency',
        innerBg: '#ffffff', innerColor: '#080400',
        sectionBg: '#080400',
        Icon: IconTransparency,
    },
];

const N = slides.length;

export default function PerformancePlatforms() {
    const containerRef = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(0);
    const slideVh = useSlideVh();

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ['start start', 'end end'],
    });

    useMotionValueEvent(scrollYProgress, 'change', (v) => {
        const i = Math.min(Math.floor(v * N), N - 1);
        if (i !== active) setActive(i);
    });

    const cur = slides[active];

    return (
        <>
            {/*
              IMPORTANT: The section itself has NO overflow:hidden — that breaks
              position:sticky in Safari/Firefox. The rounded top comes from the
              sticky div being the first visible child.
            */}
            <section
                style={{
                    background: '#080400',
                    borderTopLeftRadius: '80px',
                    borderTopRightRadius: '80px',
                    /* overflow:clip clips visually without creating a scroll container */
                    overflow: 'clip',
                }}
            >
                {/* ── scroll track: N × slide height (100vh desktop, 78vh mobile) ── */}
                <div ref={containerRef} style={{ height: `${N * slideVh}vh` }}>

                    {/* ── sticky viewport ── */}
                    <motion.div
                        className="sticky top-0"
                        style={{ height: `${slideVh}svh` }}
                        animate={{ background: cur.sectionBg }}
                        transition={{ duration: 0.55, ease: 'easeInOut' }}
                    >
                        {/*
                          Full-height two-column flex.
                          pt accounts for the fixed Navbar (~88px).
                        */}
                        <div
                            className="h-full flex flex-col lg:flex-row items-start lg:items-center
                                        justify-center lg:justify-start
                                        px-6 md:px-16 lg:px-24
                                        pt-24 pb-10 gap-6 lg:gap-16
                                        max-w-[1400px] mx-auto overflow-hidden"
                        >

                            {/* ══ LEFT — text ══ */}
                            <div className="flex flex-col gap-4 lg:gap-6 min-w-0 lg:flex-1">

                                {/* eyebrow */}
                                <div className="flex items-center gap-3">
                                    <span
                                        className="text-[10px] font-bold tracking-[0.3em] uppercase"
                                        style={{ color: '#ceff1a', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                                    >
                                        Why Choose Us
                                    </span>
                                    <div className="h-px w-10" style={{ background: 'rgba(206,255,26,0.35)' }} />
                                </div>

                                {/* animated slide content */}
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={active}
                                        initial={{ opacity: 0, y: 24 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -18 }}
                                        transition={{ duration: 0.36, ease: [0.25, 0.46, 0.45, 0.94] }}
                                        className="flex flex-col gap-5"
                                    >
                                        {/* number */}
                                        <span
                                            className="text-xs font-bold tracking-[0.24em] uppercase"
                                            style={{ color: 'rgba(206,255,26,0.45)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                                        >
                                            {cur.num} &nbsp;/&nbsp; {String(N).padStart(2, '0')}
                                        </span>

                                        {/* heading */}
                                        <h3
                                            className="font-bold leading-[1.1]"
                                            style={{
                                                fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                                fontSize: 'clamp(28px, 4vw, 60px)',
                                                color: '#fbfdfd',
                                                letterSpacing: '-0.02em',
                                                maxWidth: 640,
                                            }}
                                        >
                                            {cur.title}
                                        </h3>

                                        {/* accent */}
                                        <motion.div
                                            initial={{ scaleX: 0 }}
                                            animate={{ scaleX: 1 }}
                                            transition={{ duration: 0.38, delay: 0.1 }}
                                            className="origin-left h-[2px] w-12 rounded-full"
                                            style={{ background: '#ceff1a' }}
                                        />

                                        {/* body */}
                                        <p
                                            className="leading-[1.75]"
                                            style={{
                                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                                fontSize: 'clamp(14px, 1.4vw, 20px)',
                                                color: 'rgba(251,253,253,0.60)',
                                                maxWidth: 560,
                                            }}
                                        >
                                            {cur.body}
                                        </p>
                                    </motion.div>
                                </AnimatePresence>

                                {/* pill progress — desktop only (in flow) */}
                                <div className="hidden lg:flex gap-2 pt-2">
                                    {slides.map((_, i) => (
                                        <motion.div
                                            key={i}
                                            animate={{
                                                width: active === i ? 28 : 6,
                                                background: active === i ? '#ceff1a' : 'rgba(251,253,253,0.18)',
                                            }}
                                            transition={{ duration: 0.3 }}
                                            className="h-1.5 rounded-full"
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* ══ RIGHT — feature card (desktop only) ══ */}
                            <div className="hidden lg:flex shrink-0 items-center justify-center">
                                {/* outer container — light gray shell */}
                                <div
                                    style={{
                                        width: 400,
                                        height: 490,
                                        background: 'rgba(200,200,200,0.07)',
                                        borderRadius: 32,
                                        padding: 14,
                                        border: '1px solid rgba(255,255,255,0.09)',
                                        boxShadow: '0 40px 100px rgba(0,0,0,0.5)',
                                        position: 'relative',
                                    }}
                                >
                                    {/* inner card */}
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={active}
                                            initial={{ opacity: 0, scale: 0.93 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.96 }}
                                            transition={{ duration: 0.36, ease: [0.25, 0.46, 0.45, 0.94] }}
                                            className="w-full h-full flex flex-col items-center justify-center gap-7"
                                            style={{
                                                background: cur.innerBg,
                                                borderRadius: 22,
                                                position: 'relative',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {/* background watermark */}
                                            <span
                                                className="absolute select-none pointer-events-none font-bold"
                                                style={{
                                                    fontSize: 160,
                                                    fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                                    color: 'rgba(0,0,0,0.07)',
                                                    letterSpacing: '-0.05em',
                                                    lineHeight: 1,
                                                    bottom: -20,
                                                    right: -10,
                                                }}
                                            >
                                                {cur.num}
                                            </span>

                                            {/* icon */}
                                            <motion.div
                                                initial={{ scale: 0.78, opacity: 0 }}
                                                animate={{ scale: 1, opacity: 1 }}
                                                transition={{ duration: 0.42, delay: 0.08, ease: 'backOut' }}
                                            >
                                                <cur.Icon color={cur.innerColor} />
                                            </motion.div>

                                            {/* card label */}
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.34, delay: 0.16 }}
                                                className="flex flex-col items-center gap-1 text-center relative z-10"
                                            >
                                                <span
                                                    className="text-xl font-bold tracking-tight"
                                                    style={{
                                                        fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                                        color: cur.innerColor,
                                                    }}
                                                >
                                                    {cur.cardTitle}
                                                </span>
                                                <span
                                                    className="text-[10px] tracking-[0.2em] uppercase font-bold"
                                                    style={{
                                                        color: 'rgba(0,0,0,0.35)',
                                                        fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                                    }}
                                                >
                                                    {cur.num} of {N}
                                                </span>
                                            </motion.div>
                                        </motion.div>
                                    </AnimatePresence>
                                </div>
                            </div>
                        </div>

                        {/* pill progress — mobile only, absolutely pinned to bottom */}
                        <div className="lg:hidden absolute bottom-14 left-6 md:left-16 flex gap-2">
                            {slides.map((_, i) => (
                                <motion.div
                                    key={i}
                                    animate={{
                                        width: active === i ? 28 : 6,
                                        background: active === i ? '#ceff1a' : 'rgba(251,253,253,0.18)',
                                    }}
                                    transition={{ duration: 0.3 }}
                                    className="h-1.5 rounded-full"
                                />
                            ))}
                        </div>

                        {/* counter — top right */}
                        <div
                            className="absolute top-8 right-8 hidden md:flex items-baseline gap-1"
                            style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                        >
                            <AnimatePresence mode="wait">
                                <motion.span
                                    key={active}
                                    initial={{ y: 6, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    exit={{ y: -6, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="text-xl font-bold"
                                    style={{ color: '#ceff1a' }}
                                >
                                    {String(active + 1).padStart(2, '0')}
                                </motion.span>
                            </AnimatePresence>
                            <span className="text-sm" style={{ color: 'rgba(251,253,253,0.2)' }}>
                                / {String(N).padStart(2, '0')}
                            </span>
                        </div>

                        {/* bottom progress bar */}
                        <div className="absolute bottom-0 left-0 right-0 h-[2px]"
                            style={{ background: 'rgba(255,255,255,0.05)' }}>
                            <motion.div
                                className="h-full"
                                style={{ background: '#ceff1a' }}
                                animate={{ width: `${((active + 1) / N) * 100}%` }}
                                transition={{ duration: 0.4, ease: 'easeOut' }}
                            />
                        </div>

                        {/* scroll hint — fades after first card */}
                        <motion.div
                            animate={{ opacity: active === 0 ? 1 : 0 }}
                            transition={{ duration: 0.3 }}
                            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 pointer-events-none"
                        >
                            <span
                                className="text-[9px] tracking-[0.25em] uppercase font-bold"
                                style={{ color: 'rgba(251,253,253,0.2)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                            >
                                Scroll to explore
                            </span>
                            <motion.div
                                animate={{ y: [0, 5, 0] }}
                                transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
                            >
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M7 2v10M3 8l4 4 4-4" stroke="rgba(251,253,253,0.2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </motion.div>
                        </motion.div>
                    </motion.div>
                </div>

            </section>
        </>
    );
}
